import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi'
import { z } from 'zod'
import { unwrapSchema, type Schema } from '@omnea-app/packages/universal/references/schemaStructure'
import type { SensitiveDataCategory } from '@omnea-digital/authz-model'
import type { SchemaObject } from 'openapi3-ts/oas30'

extendZodWithOpenApi(z)

// The specs are OpenAPI 3.0, which has `nullable`; the `.openapi()` type only allows keys common to 3.0 and 3.1.
const maskedValue: SchemaObject = { nullable: true }

type AppliesWhen = (object: Readonly<Record<string, unknown>>) => boolean

// Not zod's global registry: the OpenAPI generator publishes everything in that one.
const sensitiveDataRegistry = z.registry<{ category: SensitiveDataCategory; appliesWhen?: AppliesWhen }>()

declare const sensitiveTag: unique symbol

// Type-only: nothing sets it at runtime. `withRedacted` reads it to type `redacted`.
export type SensitiveSchema = { readonly [sensitiveTag]: SensitiveDataCategory }

type WithoutOptional<S> = S extends z.ZodOptional<infer Inner> ? Inner : S

export type SensitiveKeys<Shape> = {
  [K in keyof Shape]: WithoutOptional<Shape[K]> extends SensitiveSchema ? K : never
}[keyof Shape]

/**
 * Declares an object property as holding `category`, so a response hides it from a caller who cannot see that category.
 * The published API type marks it nullable, because a masked response sends `null`.
 *
 * Do not chain `.openapi()` or `.describe()` onto the result: each clones the schema and drops the declaration.
 * Call them on the schema first, or wrap the result in `.optional()`, which keeps it reachable. Put `.nullable()`
 * on the schema you pass in: `withRedacted` types `redacted` through `.optional()` only.
 *
 * With `appliesWhen`, the property holds `category` only in an object the condition accepts.
 */
export function sensitive<T extends z.ZodType>(
  category: SensitiveDataCategory,
  schema: T,
  appliesWhen?: AppliesWhen
): T & SensitiveSchema
export function sensitive(category: SensitiveDataCategory, schema: z.ZodType, appliesWhen?: AppliesWhen): z.ZodType {
  const declared = schema.openapi(maskedValue)
  sensitiveDataRegistry.add(declared, { category, appliesWhen })
  return declared
}

// Without `object`, the category the property can hold in any object.
export const sensitiveDataCategoryOf = (
  schema: Schema,
  object?: Readonly<Record<string, unknown>>
): SensitiveDataCategory | undefined =>
  unwrapSchema(schema)
    .layers.map((layer) => sensitiveDataRegistry.get(layer))
    .find((mark) => mark !== undefined && (object === undefined || (mark.appliesWhen?.(object) ?? true)))?.category

export const sensitivePropertiesOf = (schema: z.ZodObject): Partial<Record<string, SensitiveDataCategory>> =>
  Object.fromEntries(
    Object.entries(schema.shape).flatMap(([key, property]): [string, SensitiveDataCategory][] => {
      const category = sensitiveDataCategoryOf(property)
      return category === undefined ? [] : [[key, category]]
    })
  )
